import { Box, Hide, Show } from '@chakra-ui/react'
import React, {
  createContext,
  FC,
  ReactElement,
  ReactNode,
  RefObject,
  useMemo,
  useRef,
} from 'react'

import { ContentPanel } from './ContentPanel'
import { FilterPanel } from './FilterPanel'
import { Header, HeaderProps } from './Header'
import { InformationPanel, InformationPanelProps } from './InformationPanel'
import { ListPageDesktop } from './ListPageDesktop'
import { ListPageMobile } from './ListPageMobile'
import { Pagination, PaginationProps } from './Pagination'
import { SortPanel, SortPanelProps } from './SortPanel'
import { groupReactElementsByDisplayName } from './utils'

interface ListPageType {
  ContentPanel: FC
  FilterPanel: FC
  Header: FC<HeaderProps>
  InformationPanel: FC<InformationPanelProps>
  Pagination: FC<PaginationProps>
  SortPanel: FC<SortPanelProps>
}
interface ListPageContextType {
  topRef: RefObject<HTMLElement>
}

export const ListPageContext = createContext<ListPageContextType>({
  topRef: { current: null },
})

const ListPage: FC<{ children: (ReactElement | null | false)[] }> &
  ListPageType = ({ children }) => {
  const topRef = useRef<HTMLDivElement>(null)

  const renderElement = useMemo(() => {
    const elements = children.filter((child): child is ReactElement =>
      Boolean(child),
    )
    const reactElementsByDisplayName = groupReactElementsByDisplayName(elements)

    return (displayName: string): ReactNode => {
      if (!reactElementsByDisplayName[displayName]?.length) return null

      return reactElementsByDisplayName[displayName].map((element, index) => (
        <React.Fragment key={index}>{element}</React.Fragment>
      ))
    }
  }, [children])

  return (
    <Box ref={topRef}>
      <ListPageContext.Provider value={{ topRef }}>
        <Show above="tablet">
          <ListPageDesktop renderElement={renderElement} />
        </Show>
        <Hide above="tablet">
          <ListPageMobile renderElement={renderElement} />
        </Hide>
      </ListPageContext.Provider>
    </Box>
  )
}

ListPage.Header = Header
ListPage.FilterPanel = FilterPanel
ListPage.SortPanel = SortPanel
ListPage.Pagination = Pagination
ListPage.InformationPanel = InformationPanel
ListPage.ContentPanel = ContentPanel

export { ListPage }
